export interface PostfixConfiguration {
  hostname: string;
  domain: string;
  certificate: string;
  privateKey: string;
  privateRelay: boolean;
  clientIp: string;
}

export const defaultPostfixConfiguration: PostfixConfiguration = {
  hostname: 'postfix.example.com',
  domain: 'example.com',
  certificate: '/etc/letsencrypt/live/postfix.example.com/fullchain.pem',
  privateKey: '/etc/letsencrypt/live/postfix.example.com/privkey.pem',
  privateRelay: false,
  clientIp: '',
};

function validHostname(value: string) {
  return value.length <= 253 && value.includes('.') && value.split('.').every(label => /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/i.test(label));
}

function validPrivateIp(value: string) {
  const octets = value.split('.');
  if (octets.length !== 4 || !octets.every(part => /^\d{1,3}$/.test(part) && Number(part) <= 255 && String(Number(part)) === part)) return false;
  const [a, b] = octets.map(Number);
  return a === 10 || (a === 172 && b >= 16 && b <= 31) || (a === 192 && b === 168) || a === 127;
}

export function buildPostfixConfiguration(config: PostfixConfiguration) {
  if (!validHostname(config.hostname) || !validHostname(config.domain)) throw new Error('Enter a valid fully qualified Postfix hostname and domain without spaces or newlines.');
  if (![config.certificate, config.privateKey].every(path => /^\/[a-z0-9_./-]+$/i.test(path) && !path.includes('..'))) throw new Error('Certificate paths must be absolute paths without spaces, newlines, or parent-directory segments.');
  if (config.privateRelay && !validPrivateIp(config.clientIp)) throw new Error('Enter one private IPv4 address for the AwareNow server. Broad networks and public addresses are not accepted.');
  const trusted = `127.0.0.0/8 [::1]/128${config.privateRelay && !config.clientIp.startsWith('127.') ? ` ${config.clientIp}/32` : ''}`;
  const main = `# Review and merge into /etc/postfix/main.cf. Do not overwrite an existing mail server configuration.
# Dedicated Debian/Ubuntu outbound relay with existing Dovecot authentication.
# These global restrictions block public inbound SMTP; use a separate relay host.
myhostname = ${config.hostname}
mydomain = ${config.domain}
myorigin = $mydomain
mydestination = $myhostname, localhost.$mydomain, localhost
inet_interfaces = all
inet_protocols = ipv4
mynetworks = ${trusted}
# Restrict the private SMTP listener; submission overrides this below.
smtpd_client_restrictions = permit_mynetworks, reject
smtpd_relay_restrictions = permit_mynetworks, reject_unauth_destination
smtpd_recipient_restrictions = permit_mynetworks, reject_unauth_destination
smtpd_tls_cert_file = ${config.certificate}
smtpd_tls_key_file = ${config.privateKey}
smtpd_tls_security_level = encrypt
smtpd_tls_auth_only = yes
smtpd_sasl_type = dovecot
smtpd_sasl_path = private/auth
smtpd_sasl_auth_enable = no
disable_vrfy_command = yes
# Encrypt outbound delivery when the receiving server offers STARTTLS.
smtp_tls_security_level = may
smtp_tls_CAfile = /etc/ssl/certs/ca-certificates.crt
smtp_tls_loglevel = 1
`;
  const master = `# Review and merge into /etc/postfix/master.cf; replace any existing submission stanza.
# This example uses Debian/Ubuntu's chrooted smtpd layout.
submission inet n - y - - smtpd
  -o syslog_name=postfix/submission
  -o smtpd_tls_security_level=encrypt
  -o smtpd_sasl_auth_enable=yes
  -o smtpd_client_restrictions=
  -o smtpd_relay_restrictions=permit_sasl_authenticated,reject
  -o smtpd_recipient_restrictions=permit_sasl_authenticated,reject
${config.privateRelay ? `
# Private port 25: firewall to the single AwareNow address in mynetworks.
# Replace an existing smtp stanza rather than duplicating it.
smtp inet n - y - - smtpd
` : `
# Submission only: disable/comment the existing "smtp inet ... smtpd" stanza
# if this server is dedicated to AwareNow and does not receive inbound SMTP.
`}`;
  return { main, master };
}

export function downloadPostfixFile(filename: string, contents: string) {
  const url = URL.createObjectURL(new Blob([contents], { type: 'text/plain;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
